import { parse } from 'query-string';
import { FilterType, pathFilterNames } from '../../../redux/products/types';

import { AppliedFilter } from '../../Generic/FilterAndSortBox/types';

const filterSeparator = '--';
const filterQueryParamPrefix = 'facet_';
const urlSlashEncodingKey = '_';
const urlProcentEncodingKey = '_25_';

const splitFilterStringToArray = (filterPart: string) => filterPart.split(filterSeparator);
const joinFilterArrayToString = (filterArray: string[]) => filterArray.join(filterSeparator);

// Values having the + sign in the url mean they actually have spaces
export const encodeUrlSlashes = (input: string) => input.replace(/\//g, urlSlashEncodingKey);
export const encodeUrlProcents = (input: string) => input.replace(/%/g, urlProcentEncodingKey);

// Values having spaces should use the + sign in the url=

export const decodeUrlSlashes = (input: string) => input.replace(new RegExp(`\\${urlSlashEncodingKey}`, 'g'), '/');

export const decodeUrlProcents = (input: string) => input.replace(urlProcentEncodingKey, '%');

export const normalizeFilterStateValueForUrl = (filterValues: string[]) =>
    filterValues.map(filterValue => encodeUrlProcents(encodeUrlSlashes(filterValue)));
export const normalizeFilterUrlValueForState = (filterValues: string[]) =>
    filterValues.map(filterValue => decodeUrlSlashes(decodeUrlProcents(filterValue)));

// From SEO perspective, the path filters must have a specific order, that is /brands/colors/materials.
// Therefore we need to sort the filters array, before serializing them to the URL.
// So in this dict, the sorting order of the path filter elements is defined.
// Furthermore if there is (for some reason) a new path filter, that is not defined in this object,
// it will get the last sorting by default. Until it gets defined here.
const NOT_FOUND_PATH_FILTER_SORTING_VALUE = 1000;
const pathFilterSorting = {
    [FilterType.categories]: 1,
    [FilterType.brands]: 2,
    [FilterType.colors]: 3,
    [FilterType.materials]: 4,
    [FilterType.styles]: 5,
    [FilterType.sizes]: 6,
    [FilterType.pattern]: 7,
    [FilterType.form]: 8,
    [FilterType.pillowType]: 9,
    [FilterType.lightSocketType]: 10,
    [FilterType.feature]: 11,
    [FilterType.towelType]: 12,
};

const filterPrefixes = {
    [FilterType.brands]: 'brand:',
    [FilterType.colors]: '_',
    [FilterType.materials]: '~',
    [FilterType.styles]: '*',
    [FilterType.sizes]: 'size:',
    [FilterType.pattern]: 'pattern:',
    [FilterType.form]: 'form:',
    [FilterType.pillowType]: 'pillow-type:',
    [FilterType.lightSocketType]: 'socket:',
    [FilterType.feature]: 'feature:',
    [FilterType.towelType]: 'towel-type:',
};

export const filterForTypeMapping: Record<pathFilterNames, FilterType> = {
    [pathFilterNames.colors]: FilterType.colors,
    [pathFilterNames.styles]: FilterType.styles,
    [pathFilterNames.materials]: FilterType.materials,
    [pathFilterNames.brands]: FilterType.brands,
    [pathFilterNames.categories]: FilterType.categories,
    [pathFilterNames.sizes]: FilterType.sizes,
    [pathFilterNames.pattern]: FilterType.pattern,
    [pathFilterNames.form]: FilterType.form,
    [pathFilterNames.pillowType]: FilterType.pillowType,
    [pathFilterNames.lightSocketType]: FilterType.lightSocketType,
    [pathFilterNames.feature]: FilterType.feature,
    [pathFilterNames.towelType]: FilterType.towelType,
};

export const sizeFacets = {
    BEDDING_SIZE: 'facet_filter_beddingsize',
    BED_SIZE: 'facet_bedsize',
    FITTEDSHEET_SIZE: 'facet_filter_fittedsheet_size',
    RUG_SIZE: 'facet_filter_rugsize',
    SLEEPER_DIM_SIZE: 'facet_sleeper_dim',
    PILLOW_SIZE: 'facet_filter_pillow_size',
    OLD_BASIC_SIZE: 'facet_size',
    NEW_BASIC_SIZE: 'facet_filter_size',
};

export const queryToPathFilterTypeMapping = {
    [FilterType.sizes]: sizeFacets,
};

const nonPrefixedFilter = FilterType.categories; // todo: change to categories

const parseFilterPart = (part: string) =>
    (Object.entries(filterPrefixes).find(([, prefix]) => part.startsWith(prefix)) as [FilterType, string]) || [
        nonPrefixedFilter,
        '',
    ];

export const sanitizeFiltersForURl = (filter: AppliedFilter<FilterType>) => {
    const { sizes, pattern, form, pillowType } = FilterType;

    if ([sizes, pattern, form, pillowType].includes(filter.type)) {
        return filter.values.map(filterValues => filterValues.toLocaleLowerCase().split(/\s+/).join('+'));
    }

    return filter.values.map(filterValue => filterValue.toLocaleLowerCase());
};

export const sanitizeFiltersForUIDisplay = (filtersWithoutPrefix: string, type: FilterType) => {
    const { sizes, pattern, form, pillowType } = FilterType;

    if ([sizes, pattern, form, pillowType].includes(type)) {
        return filtersWithoutPrefix.split('+').join(' ');
    }

    return filtersWithoutPrefix;
};

export const convertUrlPart = (part: string): AppliedFilter<FilterType> => {
    const [type, prefix] = parseFilterPart(part);
    const filtersWithoutPrefix = sanitizeFiltersForUIDisplay(part.substring(prefix.length), type);

    const values = splitFilterStringToArray(filtersWithoutPrefix);
    let normalizedValues = normalizeFilterUrlValueForState(values);

    if (type === FilterType.sizes) {
        // words that must be uppercase
        const upperCaseSubstrings = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'd'];
        normalizedValues = normalizedValues.map(v => {
            let newVal = v;
            upperCaseSubstrings.forEach(substr => {
                newVal = newVal.replace(new RegExp(`\\b${substr}\\b`), substr.toLocaleUpperCase());
            });
            newVal = newVal.charAt(0).toLocaleUpperCase() + newVal.slice(1);
            return newVal;
        });
    }

    return {
        name: pathFilterNames[type],
        type,
        values: normalizedValues,
    };
};

const nonUnicodeAlphaNum = /^[^0-9\p{L}]+$/gu;

const isOnlyNonAlphaNum = (urlPath: string): boolean => {
    const nonUnicodeAlphanumSubParts = urlPath.split(nonUnicodeAlphaNum);
    return nonUnicodeAlphanumSubParts.filter(nonUnicodeSubPart => nonUnicodeSubPart.length > 0).length > 0;
};

export const convertUrlToFilters = (urlPart: string): AppliedFilter<FilterType>[] => {
    const partArray = urlPart.split('/').filter(value => !!value && isOnlyNonAlphaNum(value));
    return partArray.map(convertUrlPart);
};

const convertFilterToUrlPart = (filter: AppliedFilter<FilterType>) => {
    const filterPrefix = filterPrefixes[filter.type] || '';
    const values = sanitizeFiltersForURl(filter);

    const normalizedValues = normalizeFilterStateValueForUrl(values);

    return `/${filterPrefix}${joinFilterArrayToString(normalizedValues)}`;
};

const pathFilterTypeComparator = (filterA: AppliedFilter, filterB: AppliedFilter): number => {
    const filterAVal = pathFilterSorting[filterA.type] || NOT_FOUND_PATH_FILTER_SORTING_VALUE;
    const filterBVal = pathFilterSorting[filterB.type] || NOT_FOUND_PATH_FILTER_SORTING_VALUE;
    return filterAVal - filterBVal;
};

export const convertFilterObjectToUrl = (filters: AppliedFilter[]): string => {
    const urlString = filters
        .filter(filter => filter.type !== FilterType.query)
        .sort(pathFilterTypeComparator)
        .filter(filter => filter.values.length > 0)
        .map(convertFilterToUrlPart)
        .reduce((path, filterString) => `${path}${filterString}`, '');

    return urlString || '/';
};

export const getPathFilterEntryForQueryFilter = (queryFilterKey: string) => {
    const pathFilterEntry =
        Object.entries(queryToPathFilterTypeMapping).find(([, queryFilters]) =>
            Object.values(queryFilters).includes(queryFilterKey)
        ) || [];

    const [pathFilterType = null] = pathFilterEntry;
    const pathFilterKey = pathFilterType ? pathFilterNames[pathFilterType] : null;

    return [pathFilterType, pathFilterKey];
};

export const convertQueryStringToFilters = (queryString: string): AppliedFilter<FilterType>[][] => {
    const query = parse(queryString);

    return Object.keys(query)
        .filter(item => item.startsWith(filterQueryParamPrefix))
        .filter(queryFilterKey => typeof query[queryFilterKey] === 'string')
        .map((queryFilterKey): AppliedFilter => {
            const [pathFilterType, pathFilterKey] = getPathFilterEntryForQueryFilter(queryFilterKey);

            const queryValue = query[queryFilterKey] as string;
            const filterValues = queryValue.includes(filterSeparator)
                ? splitFilterStringToArray(queryValue)
                : [queryValue];

            const filterType = (pathFilterType as FilterType) || FilterType.query;
            const filterKey = pathFilterKey || queryFilterKey;

            return {
                name: filterKey,
                type: filterType,
                values: filterValues,
            };
        })
        .reduce(
            (acc, filter) => {
                const [queryFilters, queryToPathFilters] = acc as AppliedFilter[][];

                if (filter.type === FilterType.query) {
                    queryFilters.push(filter);
                } else {
                    queryToPathFilters.push(filter);
                }

                return acc;
            },
            [[], []]
        );
};

export const convertQueryFiltersToObject = (queryFilters: AppliedFilter[]) =>
    queryFilters.reduce((acc, filter) => {
        if (filter.type === FilterType.query && filter.values.length > 0) {
            acc[filter.name] = joinFilterArrayToString(filter.values).toLocaleLowerCase();
        }
        return acc;
    }, {});
